import axios from "axios"
import { useAuthStore } from "@/store/authStore"
import { toast } from "sonner"
import { isProPlanRequired, isWalletReadOnly } from "@/lib/planCodes"
import { hasProPlan } from "@/lib/planNames"

/** Raiz da API versionada, ex.: https://api.exemplo.com/api/v1 */
export const API_BASE_URL = (import.meta.env.VITE_API_URL || "http://localhost:5000") + "/api/v1"

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  // A API só aceita Bearer (ADR-002). O único cookie é o do refresh token, HttpOnly e SameSite=Strict, usado em
  // /auth/refresh-token e /auth/logout: a API fica em api.fincontrol.dev.br, o mesmo site do frontend (ADR-019).
  withCredentials: true,
})

// Attach Authorization header from in-memory token on every request.
// accessToken is NOT persisted to localStorage (security). On page reload it
// starts null — the 401 interceptor below handles silent refresh on first request.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers["Authorization"] = `Bearer ${token}`
  return config
})

// Shared refresh promise — prevents multiple simultaneous 401s from each
// triggering their own refresh call (race condition on page reload).
let refreshPromise: Promise<string> | null = null

/**
 * Renova o access token pelo cookie HttpOnly de refresh e devolve o novo. Chamadas simultâneas compartilham a mesma
 * renovação. Também usada fora do axios (fetch do chat em fluxo).
 */
export function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    // Token deixado no localStorage por versões antigas: vai uma única vez, e a resposta grava o cookie (ADR-019)
    const legacy = useAuthStore.getState().legacyRefreshToken
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh-token`, legacy ? { refreshToken: legacy } : {}, { withCredentials: true })
      .then((res) => {
        const { accessToken, user } = res.data
        const store = useAuthStore.getState()
        store.setAuth(user, accessToken)
        store.clearLegacyRefreshToken()
        return accessToken as string
      })
      .catch((err) => {
        // Recusado pelo servidor: o token antigo não serve mais. Erro de rede mantém para a próxima tentativa.
        if (err?.response) useAuthStore.getState().clearLegacyRefreshToken()
        throw err
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config

    // Plano rebaixado (ex.: assinatura cancelada): o store ainda diz Pro. Sincroniza para que
    // ProRoute e o menu passem a exibir o bloqueio sem exigir novo login.
    if (error.response?.status === 403 && isProPlanRequired(error.response.data)) {
      const { user, setAuth } = useAuthStore.getState()
      if (user && hasProPlan(user)) setAuth({ ...user, planType: "Free", effectivePlan: "Free" })
      return Promise.reject(error)
    }

    // Gravação recusada na carteira somente leitura: uma mensagem só, com o motivo, por cima do erro da tela
    if (error.response?.status === 403 && isWalletReadOnly(error.response.data)) {
      toast.error("Esta carteira está somente leitura.", {
        id: "wallet-read-only",
        description: error.response.data?.error,
      })
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true

      try {
        const newAccessToken = await refreshAccessToken()
        original.headers["Authorization"] = `Bearer ${newAccessToken}`
        return api(original)
      } catch {
        useAuthStore.getState().logout()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)
